import React, { useState } from 'react';
import { Minus, Plus, X } from 'react-feather';

import { useOrderCreateStore } from '@/Order/store/orderCreateStore';
import { multiplyPrice } from '@/shared/utils/priceUtils';

import AddProductModal from './AddProductModal';

const AddOrderProducts: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const {
    selectedProducts,
    removeProduct,
    updateProductQuantity,
    getTotalQuantity,
    currencyCode,
    setCurrencyCode,
    getProductPrice,
    getCurrencySymbol,
    formatPrice,
    finalPaymentAmount,
  } = useOrderCreateStore();

  const handleRemoveProduct = (optionUuid: string) => {
    removeProduct(optionUuid);
  };

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleQuantityIncrease = (
    optionUuid: string,
    currentQuantity: number,
  ) => {
    updateProductQuantity(optionUuid, currentQuantity + 1);
  };

  const handleQuantityDecrease = (
    optionUuid: string,
    currentQuantity: number,
  ) => {
    const product = selectedProducts.find((p) => p.optionUuid === optionUuid);
    if (product && currentQuantity > product.minimumOrderQuantity) {
      updateProductQuantity(optionUuid, currentQuantity - 1);
    }
  };

  const handleQuantityInputChange = (optionUuid: string, value: string) => {
    const product = selectedProducts.find((p) => p.optionUuid === optionUuid);
    const quantity = parseInt(value, 10) || product?.minimumOrderQuantity || 1;
    const minQuantity = product?.minimumOrderQuantity || 1;

    if (quantity >= minQuantity) {
      updateProductQuantity(optionUuid, quantity);
    }
  };

  const handleCurrencyChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setCurrencyCode(event.target.value as 'USD' | 'KRW');
  };

  const getProductTotalPrice = (product: any) => {
    const unitPrice = getProductPrice(product.productPriceList, currencyCode);
    return multiplyPrice(unitPrice, product.quantity, currencyCode);
  };

  return (
    <>
      {showModal && <AddProductModal onClose={handleCloseModal} />}

      <div className="flex flex-col rounded border border-gray-200 bg-white shadow-sm">
        <div className="flex justify-between px-5 py-5">
          <div className="flex items-center gap-4">
            <h2 className="text-base font-medium text-[#1D1D1D]">
              Order product
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#646464]">Currency:</span>
              <select
                value={currencyCode}
                onChange={handleCurrencyChange}
                className="rounded border border-[#DDDDDD] px-2 py-1 text-sm focus:outline-none"
              >
                <option value="KRW">KRW (₩)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>
          </div>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-1 bg-transparent text-sm font-medium text-[#8F689A]"
            onClick={handleShowModal}
          >
            <Plus size={14} className="text-[#8F689A]" />
            Add
          </button>
        </div>

        <div className="w-full">
          {selectedProducts.length === 0 ? (
            <div className="border-t border-[#DDDDDD] px-4 py-8 text-center text-sm text-gray-500">
              Add products to the order by clicking the `Add` button.
            </div>
          ) : (
            <>
              {selectedProducts.map((product) => (
                <div
                  key={product.optionUuid}
                  className="flex gap-3 border-t border-[#DDDDDD] px-4 py-6"
                >
                  <img
                    src={product.thumbnailUrl || '/img/logo_b2b.svg'}
                    alt="product"
                    className="h-16 w-16 rounded"
                  />
                  <div className="w-full">
                    <div className="flex justify-between">
                      <p className="text-base leading-[160%]">
                        {product.title}
                      </p>
                      <button
                        type="button"
                        className="ml-3 h-5 w-5 cursor-pointer bg-transparent"
                        onClick={() => handleRemoveProduct(product.optionUuid)}
                      >
                        <X size={20} className="text-[#999999]" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-1 text-xs">
                      {product.optionNameKey1 && product.optionNameValue1 && (
                        <p className="rounded bg-gray-100 px-2 py-1 text-gray-800">
                          {product.optionNameValue1}
                        </p>
                      )}
                      {product.optionNameKey2 && product.optionNameValue2 && (
                        <p className="rounded bg-gray-100 px-2 py-1 text-gray-800">
                          {product.optionNameValue2}
                        </p>
                      )}
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex w-32 justify-between rounded border border-[#DDDDDD]">
                        <button
                          type="button"
                          className="flex w-8 cursor-pointer items-center justify-center border-r border-[#DDDDDD] bg-transparent py-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() =>
                            handleQuantityDecrease(
                              product.optionUuid,
                              product.quantity,
                            )
                          }
                          disabled={
                            product.quantity <= product.minimumOrderQuantity
                          }
                        >
                          <Minus size={16} className="text-[#666666]" />
                        </button>
                        <input
                          type="number"
                          className="max-w-[50%] text-center outline-none"
                          value={product.quantity}
                          onChange={(e) =>
                            handleQuantityInputChange(
                              product.optionUuid,
                              e.target.value,
                            )
                          }
                          min={product.minimumOrderQuantity}
                        />
                        <button
                          type="button"
                          className="flex w-8 cursor-pointer items-center justify-center border-l border-[#DDDDDD] bg-transparent py-2 hover:bg-gray-50"
                          onClick={() =>
                            handleQuantityIncrease(
                              product.optionUuid,
                              product.quantity,
                            )
                          }
                        >
                          <Plus size={16} className="text-[#666666]" />
                        </button>
                      </div>
                      <div className="flex gap-1 text-base text-[#1D1D1D]">
                        <span>{getCurrencySymbol()}</span>
                        <p>{formatPrice(getProductTotalPrice(product))}</p>
                      </div>
                    </div>
                    {product.minimumOrderQuantity > 1 && (
                      <div className="mt-2 text-xs text-[#666666]">
                        MOQ: {product.minimumOrderQuantity}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="space-y-2 border-t border-[#F1F1F1] px-4 py-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm text-[#646464]">Total Quantity</h4>
                  <div className="flex gap-1 text-sm font-medium text-[#1D1D1D]">
                    <p>{getTotalQuantity()}</p>
                    <span>items</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <h4 className="text-sm text-[#646464]">Total Amount</h4>
                  <div className="flex gap-1 text-base font-semibold text-[#1D1D1D]">
                    <span>{getCurrencySymbol()}</span>
                    <p>{formatPrice(finalPaymentAmount)}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AddOrderProducts;
